import {
  AltRoute,
  ArrowForward,
  Download,
  Pause,
  PhoneCallbackOutlined,
  PlayArrow,
  Upload,
} from '@mui/icons-material';
import CloseIcon from '@mui/icons-material/Close';
import PhoneForwardedOutlined from '@mui/icons-material/PhoneForwardedOutlined';
import {
  Box,
  Button,
  Chip,
  Drawer,
  IconButton,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNotify } from 'react-admin';
import {
  CallCategory,
  CallDirection,
  CallLog,
  CallLogStatus,
  Conference,
  ConferenceNote,
  TransferType,
} from '../../../../interfaces/calls';
import voiceService from '../../../../services/appointments/calls';
import { formatWithTimezone } from '../../../../util/dateFormat';
import { PhoneOutgoing } from '../../../common/PhoneOutgoing';

// Constants
const PLAYBACK_SPEEDS = [1, 1.5, 2, 3] as const;
type PlaybackSpeed = (typeof PLAYBACK_SPEEDS)[number];

interface ConferenceDetailDrawerProps {
  open: boolean;
  conference: Conference | null;
  onClose: () => void;
  onNoteAdded?: (note: ConferenceNote) => void;
}

export const ConferenceDetailDrawer: React.FC<ConferenceDetailDrawerProps> = ({
  open,
  conference,
  onClose,
  onNoteAdded,
}) => {
  const [newNote, setNewNote] = useState<string>('');
  const [isPlayingRecording, setIsPlayingRecording] = useState<boolean>(false);
  const [recordingProgress, setRecordingProgress] = useState<number>(0);
  const [currentTime, setCurrentTime] = useState<string>('00:00');
  const [isSavingNote, setIsSavingNote] = useState<boolean>(false);
  const [playbackSpeed, setPlaybackSpeed] = useState<PlaybackSpeed>(PLAYBACK_SPEEDS[0]);

  const notify = useNotify();
  const audioRef = useRef<HTMLAudioElement>(null);
  const progressIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const formatTime = useCallback((timeInSeconds: number) => {
    const minutes = Math.floor(timeInSeconds / 60);
    const seconds = Math.floor(timeInSeconds % 60);
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  }, []);

  const updateProgress = useCallback(() => {
    if (audioRef.current) {
      const audio = audioRef.current;
      const progress = (audio.currentTime / audio.duration) * 100;
      setRecordingProgress(progress || 0);
      setCurrentTime(formatTime(audio.currentTime || 0));
    }
  }, [formatTime]);

  const stopProgressUpdates = useCallback(() => {
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
      progressIntervalRef.current = null;
    }
  }, []);

  const startProgressUpdates = useCallback(() => {
    stopProgressUpdates();
    progressIntervalRef.current = setInterval(updateProgress, 100);
  }, [updateProgress, stopProgressUpdates]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || !conference?.recordingUrl) {
      return;
    }

    const handleLoadedMetadata = () => {
      updateProgress();
    };

    const handleEnded = () => {
      setIsPlayingRecording(false);
      setRecordingProgress(0);
      setCurrentTime('00:00');
      stopProgressUpdates();
    };

    const handleError = () => {
      setIsPlayingRecording(false);
      stopProgressUpdates();
      notify('Error loading audio recording', 'error');
    };

    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('error', handleError);

    return () => {
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('error', handleError);
      stopProgressUpdates();
    };
  }, [conference?.recordingUrl, notify, updateProgress, stopProgressUpdates]);

  const handleSpeedChange = useCallback(() => {
    const currentIndex = PLAYBACK_SPEEDS.indexOf(playbackSpeed);
    const nextIndex = (currentIndex + 1) % PLAYBACK_SPEEDS.length;
    const newSpeed = PLAYBACK_SPEEDS[nextIndex];

    setPlaybackSpeed(newSpeed);

    if (audioRef.current) {
      audioRef.current.playbackRate = newSpeed;
    }
  }, [playbackSpeed]);

  useEffect(() => {
    const audio = audioRef.current;
    return () => {
      stopProgressUpdates();
      if (audio) {
        audio.pause();
      }
    };
  }, [stopProgressUpdates]);

  if (!conference) {
    return null;
  }

  const firstCall = conference.calls[0];
  const isIncoming = firstCall?.direction === CallDirection.INBOUND;

  const formatDuration = (duration?: number) => {
    if (typeof duration !== 'number' || isNaN(duration)) return '0 minutes, 0 seconds';
    const minutes = Math.floor(duration / 60);
    const seconds = duration % 60;
    return `${minutes} minutes, ${seconds} seconds`;
  };

  const getConferenceDuration = () => {
    const totalDuration = conference.calls.reduce((sum, call) => sum + (call.duration || 0), 0);
    return totalDuration;
  };

  const getRecordingDuration = () => {
    if (conference?.recordingDuration) {
      const minutes = Math.floor(conference?.recordingDuration / 60);
      const seconds = conference?.recordingDuration % 60;
      return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    }
    return '00:00';
  };

  const getStatusColor = (status: CallLogStatus) => {
    switch (status) {
      case CallLogStatus.COMPLETED:
        return '#30A46C16';
      case CallLogStatus.FAILED:
      case CallLogStatus.NO_ANSWER:
      case CallLogStatus.CANCELED:
        return '#D3262616';
      case CallLogStatus.BUSY:
        return '#C7961225';
      default:
        return '#389FBA16';
    }
  };

  const getStatusTextColor = (status: CallLogStatus) => {
    switch (status) {
      case CallLogStatus.COMPLETED:
        return '#30A46C';
      case CallLogStatus.BUSY:
        return '#C79612';
      case CallLogStatus.FAILED:
      case CallLogStatus.NO_ANSWER:
      case CallLogStatus.CANCELED:
        return '#D32626';
      default:
        return '#389FBA';
    }
  };

  const formatCallDuration = (duration?: number) => {
    if (typeof duration !== 'number' || isNaN(duration) || duration === 0) {
      return '';
    }
    const minutes = Math.floor(duration / 60);
    const seconds = duration % 60;
    return `${minutes}m ${seconds}s`;
  };

  const getCallIcon = (call: CallLog) => {
    if (call.isMainLineInvolved) {
      return <AltRoute sx={{ color: '#666', fontSize: 20 }} />;
    }

    if (call.category === CallCategory.BROWSER_CALL) {
      return <ArrowForward sx={{ color: '#666', fontSize: 20 }} />;
    }

    if (call.transferType) {
      if (call.transferType === TransferType.BLIND) {
        return <PhoneForwardedOutlined sx={{ color: '#666', fontSize: 20 }} />;
      }

      if (call.transferType === TransferType.WARM) {
        return <PhoneForwardedOutlined sx={{ color: '#666', fontSize: 20 }} />;
      }
    }

    if (call.direction === CallDirection.INBOUND) {
      return <PhoneCallbackOutlined sx={{ color: '#AF77BE', fontSize: 20 }} />;
    }
    if (call.direction === CallDirection.OUTBOUND) {
      return <PhoneOutgoing sx={{ color: '#389FBA', fontSize: 20 }} />;
    }

    return <PhoneOutgoing sx={{ color: '#389FBA', fontSize: 20 }} />;
  };

  const handleProgressBarClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (!audioRef.current) {
      return;
    }
    const audio = audioRef.current;
    const progressBar = event.currentTarget;
    const rect = progressBar.getBoundingClientRect();
    const clickX = event.clientX - rect.left;
    const clickPercent = clickX / rect.width;
    const newTime = clickPercent * (audio.duration || 0);
    audio.currentTime = newTime;
    updateProgress();
  };

  const handlePlayPause = async () => {
    if (!audioRef.current || !conference?.recordingUrl) {
      notify('Recording not available', 'error');
      return;
    }

    const audio = audioRef.current;

    try {
      if (isPlayingRecording) {
        audio.pause();
        stopProgressUpdates();
        setIsPlayingRecording(false);
      } else {
        if (audio.src !== conference?.recordingUrl) {
          audio.src = conference?.recordingUrl;
        }

        if (audio.readyState < 2) {
          await new Promise((resolve, reject) => {
            const handleCanPlay = () => {
              audio.removeEventListener('canplay', handleCanPlay);
              audio.removeEventListener('error', handleError);
              resolve(undefined);
            };
            const handleError = (e: Event) => {
              audio.removeEventListener('canplay', handleCanPlay);
              audio.removeEventListener('error', handleError);
              reject(e);
            };
            audio.addEventListener('canplay', handleCanPlay);
            audio.addEventListener('error', handleError);
          });
        }

        audio.playbackRate = playbackSpeed;

        const playPromise = audio.play();
        if (playPromise) {
          await playPromise;
        }

        startProgressUpdates();
        setIsPlayingRecording(true);
      }
    } catch (error) {
      console.error('Error playing audio:', error);
      setIsPlayingRecording(false);
      stopProgressUpdates();
      notify('Error playing recording: ' + (error as Error).message, 'error');
    }
  };

  const handleDownloadRecording = () => {
    if (conference?.recordingUrl) {
      window.open(conference?.recordingUrl, '_blank');
    }
  };

  const handleAddNote = async () => {
    if (!newNote.trim() || !conference) {
      return;
    }

    setIsSavingNote(true);
    try {
      const response = await voiceService.addNote(conference.conferenceId, {
        content: newNote,
      });

      if (response.success && response.data) {
        notify('Note added successfully', 'success');
        setNewNote('');
        if (onNoteAdded) {
          onNoteAdded(response.data);
        }
      } else {
        notify('Failed to add note', 'error');
      }
    } catch (error) {
      console.error('Error adding note:', error);
      notify('Failed to add note', 'error');
    } finally {
      setIsSavingNote(false);
    }
  };

  const conferenceNotes = conference.notes || [];

  return (
    <>
      <audio ref={audioRef} style={{ display: 'none' }} preload='metadata' controls={false} />
      <Drawer anchor='right' open={open} onClose={onClose}>
        <Box
          sx={{
            width: '800px',
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
          }}>
          {/* Header Section */}
          <Box>
            <Box
              sx={{
                backgroundColor: '#f8f9fa',
                borderBottom: '1px solid #e9ecef',
                display: 'flex',
                justifyContent: 'flex-end',
                padding: '4px 10px',
              }}>
              <IconButton onClick={onClose} size='small'>
                <CloseIcon />
              </IconButton>
            </Box>

            <Box
              display='flex'
              alignItems='center'
              justifyContent='space-between'
              padding='0 24px'
              marginTop={2}>
              <Box display='flex' alignItems='center' gap={1}>
                {isIncoming ? (
                  <PhoneCallbackOutlined sx={{ color: '#AF77BE', fontSize: 20 }} />
                ) : (
                  <PhoneOutgoing sx={{ color: '#389FBA', fontSize: 20 }} />
                )}

                <Typography variant='h6' fontWeight={600}>
                  {isIncoming ? 'Inbound Call' : 'Outbound Call'}
                </Typography>
              </Box>
            </Box>

            {/* Conference Information */}
            <Box pt={3} pb={2} borderBottom='2px solid #e9ecef'>
              <Box display='flex' justifyContent='space-between' gap={2} pl={3} pr={3} pb={1}>
                <Box flex={1}>
                  <Typography variant='subtitle2' color='textSecondary' gutterBottom>
                    From:
                  </Typography>
                  <Box
                    border='1px solid #E0E0E0'
                    p={1}
                    borderRadius='4px'
                    display='flex'
                    flexDirection='column'>
                    <Typography variant='subtitle1'>
                      {firstCall?.caller?.name || 'Unknown'}
                    </Typography>
                    <Typography variant='body2' color='textSecondary'>
                      {firstCall?.caller?.isFromBrowser
                        ? 'Browser'
                        : firstCall?.caller?.phoneNumber || ''}
                    </Typography>
                    <Typography variant='caption' color='textSecondary'>
                      {firstCall?.caller?.isAgent
                        ? 'Agent'
                        : firstCall?.caller?.id
                        ? ' • Member'
                        : ''}
                      {firstCall?.caller?.id && firstCall?.caller?.isAgent ? ' • ' : ''}
                      {firstCall?.caller?.id ? `ID: ${firstCall.caller.id}` : ''}
                    </Typography>
                  </Box>
                </Box>

                {/* To Section */}
                <Box flex={1}>
                  <Typography variant='subtitle2' color='textSecondary' gutterBottom>
                    To:
                  </Typography>
                  <Box
                    border='1px solid #E0E0E0'
                    p={1}
                    borderRadius='4px'
                    display='flex'
                    flexDirection='column'>
                    <Typography variant='subtitle1'>
                      {firstCall?.callee?.name || 'Unknown'}
                    </Typography>
                    <Typography variant='body2' color='textSecondary'>
                      {firstCall?.callee?.isToBrowser
                        ? 'Browser'
                        : firstCall?.callee?.phoneNumber || ''}
                    </Typography>
                    <Typography variant='caption' color='textSecondary'>
                      {firstCall?.callee?.isAgent
                        ? 'Agent'
                        : firstCall?.callee?.id
                        ? ' • Member'
                        : ''}
                      {firstCall?.callee?.id && firstCall?.callee?.isAgent ? ' • ' : ''}
                      {firstCall?.callee?.id ? `ID: ${firstCall.callee.id}` : ''}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>

          {/* Content Section */}
          <Box sx={{ flex: 1, overflowY: 'auto', padding: 0 }}>
            {/* Duration and Date/Time */}
            <Box
              display='flex'
              flexDirection='column'
              gap={1}
              pl={3}
              pr={3}
              pt={3}
              pb={2}
              borderBottom='2px solid #e9ecef'>
              <Box display='flex' gap={1}>
                <Typography variant='body1' color='textSecondary' gutterBottom width='12rem'>
                  Duration:
                </Typography>
                <Typography variant='body1'>{formatDuration(getConferenceDuration())}</Typography>
              </Box>

              <Box display='flex' gap={1}>
                <Typography variant='body1' color='textSecondary' gutterBottom width='12rem'>
                  Date / Time:
                </Typography>
                <Typography variant='body1'>
                  {formatWithTimezone(
                    conference.createdAt,
                    undefined,
                    'MMM DD, YYYY [at] HH:mm [PT]',
                  )}
                </Typography>
              </Box>
            </Box>

            {/* Conference Recording */}
            {conference?.recordingUrl && (
              <Box p={3} borderBottom='1px solid #e9ecef'>
                <Typography
                  variant='subtitle1'
                  fontWeight='bold'
                  mb={2}
                  sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  Conference Recording
                </Typography>

                <Box
                  sx={{
                    borderRadius: 2,
                    p: 2,
                    border: '1px solid #e9ecef',
                  }}>
                  <Box display='flex' alignItems='center' gap={1}>
                    <IconButton
                      onClick={handlePlayPause}
                      sx={{
                        backgroundColor: '#389FBA',
                        color: 'white',
                        '&:hover': { backgroundColor: '#2d7a96' },
                        width: 32,
                        height: 32,
                      }}>
                      {isPlayingRecording ? <Pause /> : <PlayArrow />}
                    </IconButton>

                    <Button
                      onClick={handleSpeedChange}
                      size='small'
                      sx={{
                        minWidth: '40px',
                        height: '28px',
                        backgroundColor: '#f5f5f5',
                        color: '#666',
                        border: '1px solid #ddd',
                        '&:hover': {
                          backgroundColor: '#e0e0e0',
                          border: '1px solid #bbb',
                        },
                        fontSize: '11px',
                        fontWeight: 600,
                        borderRadius: '6px',
                      }}>
                      {playbackSpeed}x
                    </Button>

                    <Box flex={1} ml={1}>
                      <Box
                        onClick={handleProgressBarClick}
                        sx={{
                          cursor: 'pointer',
                          '&:hover .MuiLinearProgress-bar': {
                            backgroundColor: '#2d7a96',
                          },
                        }}>
                        <LinearProgress
                          variant='determinate'
                          value={recordingProgress}
                          sx={{
                            height: 6,
                            borderRadius: 3,
                            backgroundColor: '#e9ecef',
                            '& .MuiLinearProgress-bar': {
                              backgroundColor: '#389FBA',
                              transition: 'background-color 0.2s',
                            },
                          }}
                        />
                      </Box>
                      <Box display='flex' justifyContent='space-between' mt={0.5}>
                        <Typography variant='caption' color='textSecondary'>
                          {currentTime}
                        </Typography>
                        <Typography variant='caption' color='textSecondary'>
                          {getRecordingDuration()}
                        </Typography>
                      </Box>
                    </Box>

                    <IconButton onClick={handleDownloadRecording} size='small' sx={{ ml: 1 }}>
                      <Download />
                    </IconButton>
                  </Box>
                </Box>
              </Box>
            )}

            {/* Call Logs Section */}
            <Box p={3} borderBottom='1px solid #e9ecef'>
              <Typography variant='subtitle1' fontWeight='bold' mb={2}>
                Log
              </Typography>

              <Box
                sx={{
                  border: '1px solid #e9ecef',
                  borderRadius: '8px',
                  overflow: 'hidden',
                }}>
                <Table size='small'>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                      <TableCell sx={{ fontWeight: 'bold', padding: '12px' }}>From</TableCell>
                      <TableCell
                        sx={{ fontWeight: 'bold', padding: '12px', width: '60px' }}></TableCell>
                      <TableCell sx={{ fontWeight: 'bold', padding: '12px' }}>To</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', padding: '12px' }}>Status</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', padding: '12px' }}>Time</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {conference.calls.map((call) => {
                      return (
                        <TableRow
                          key={call.id}
                          sx={{
                            '&:last-child td': { borderBottom: 0 },
                            '&:hover': { backgroundColor: '#f8f9fa' },
                          }}>
                          <TableCell sx={{ padding: '12px' }}>
                            <Box>
                              <Typography variant='body2'>
                                {call.caller?.name || 'Unknown'}
                              </Typography>
                              <Typography variant='caption' color='textSecondary'>
                                {call.caller?.isFromBrowser
                                  ? 'Browser'
                                  : call.caller?.phoneNumber || ''}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell sx={{ padding: '12px', textAlign: 'center' }}>
                            {getCallIcon(call)}
                          </TableCell>
                          <TableCell sx={{ padding: '12px' }}>
                            <Box>
                              <Typography variant='body2'>
                                {call.callee?.name || 'Unknown'}
                              </Typography>
                              <Typography variant='caption' color='textSecondary'>
                                {call.callee?.isToBrowser
                                  ? 'Browser'
                                  : call.callee?.phoneNumber || ''}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell sx={{ padding: '12px' }}>
                            <Chip
                              label={call.status.toLowerCase().replace('_', ' ')}
                              size='small'
                              sx={{
                                backgroundColor: getStatusColor(call.status),
                                color: getStatusTextColor(call.status),
                                textTransform: 'lowercase',
                                height: '24px',
                              }}
                            />
                          </TableCell>
                          <TableCell sx={{ padding: '12px' }}>
                            <Typography variant='body2' color='textSecondary'>
                              {formatWithTimezone(call.createdAt, undefined, 'h:mm A')}
                            </Typography>
                            <Typography variant='caption' color='textSecondary'>
                              {formatCallDuration(call.duration)}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </Box>
            </Box>

            {/* Notes Section */}
            <Box p={3}>
              <Typography variant='subtitle1' fontWeight='bold' mb={2}>
                Notes
              </Typography>

              {conferenceNotes.length > 0 ? (
                <Box sx={{ maxHeight: '300px', overflowY: 'auto', mb: 2 }}>
                  {conferenceNotes.map((note) => (
                    <Box
                      key={note.id}
                      sx={{
                        borderRadius: 1,
                        p: 2,
                        border: '1px solid #e9ecef',
                        mb: 2,
                        '&:last-child': { mb: 0 },
                      }}>
                      <Box display='flex' alignItems='center' gap={1} mb={1}>
                        <Typography variant='caption' color='textSecondary'>
                          <strong>Provider {note.coreProviderId || 'Unknown'}</strong> •{' '}
                          {formatWithTimezone(
                            note.createdAt,
                            undefined,
                            'MMM DD, YYYY [at] H:mm A',
                          )}
                        </Typography>
                      </Box>
                      <Typography variant='body2'>{note.content}</Typography>
                    </Box>
                  ))}
                </Box>
              ) : (
                <Typography
                  variant='body2'
                  color='textSecondary'
                  sx={{ mb: 2, fontStyle: 'italic' }}>
                  No notes have been added to this call yet.
                </Typography>
              )}

              <TextField
                fullWidth
                multiline
                rows={3}
                placeholder='Add a note about this call...'
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
                variant='outlined'
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  },
                }}
              />
            </Box>
          </Box>

          {/* Footer Section */}
          <Box
            sx={{ padding: '16px 24px', borderTop: '1px solid #e9ecef', backgroundColor: '#fff' }}>
            <Button
              variant='contained'
              fullWidth
              startIcon={<Upload />}
              onClick={handleAddNote}
              disabled={!newNote.trim() || isSavingNote}
              sx={{
                backgroundColor: '#007bff',
                textTransform: 'none',
                '&:hover': { backgroundColor: '#0056b3' },
              }}>
              {isSavingNote ? 'Adding Note...' : 'Add Note'}
            </Button>
          </Box>
        </Box>
      </Drawer>
    </>
  );
};
